import { useEffect, useState } from 'react'
import axios from 'axios'
import cookies from 'js-cookie'

type Nullable<T> = T | null

type UseLysUserParams = {
  mdmClientNumber?: number | null
  lysHomepageEnabled: boolean
}

type LysResponse = {
  data?: {
    data?: {
      type?: number | string
      data?: {
        type?: number | string
      }
    }
  }
}

type UseLysUserReturn = {
  isCheckingLys: boolean
  isLysUser: boolean
  lysProgramType: Nullable<number>
}

export const useLysUser = ({
  mdmClientNumber,
  lysHomepageEnabled
}: UseLysUserParams): UseLysUserReturn => {
  const [isCheckingLys, setIsCheckingLys] = useState(false)
  const [isLysUser, setIsLysUser] = useState(false)
  const [lysProgramType, setLysProgramType] = useState<Nullable<number>>(null)

  useEffect(() => {
    let cancelled = false

    const resetLysState = () => {
      setIsLysUser(false)
      setLysProgramType(null)
    }

    const checkLysUser = async () => {
      if (!mdmClientNumber) {
        resetLysState()
        return
      }

      const token = cookies.get('api_token')

      if (!token) {
        resetLysState()
        return
      }

      setIsCheckingLys(true)

      try {
        const response = await axios.get<LysResponse>(
          `/api/v2/loyaltyProgram/lys/loyalty-accounts?customer=${mdmClientNumber}`,
          {
            withCredentials: true,
            headers: {
              accept: 'application/json, text/plain, */*',
              Authorization: `Bearer ${token}`
            }
          }
        )

        if (cancelled) {
          return
        }

        const account = response.data?.data?.data

        setIsLysUser(true)
        setLysProgramType(account?.type ? Number(account.type) : null)
      } catch {
        if (!cancelled) {
          resetLysState()
        }
      } finally {
        if (!cancelled) {
          setIsCheckingLys(false)
        }
      }
    }

    if (lysHomepageEnabled) {
      checkLysUser()
    } else {
      resetLysState()
    }

    return () => {
      cancelled = true
    }
  }, [mdmClientNumber, lysHomepageEnabled])

  return {
    isCheckingLys,
    isLysUser,
    lysProgramType
  }
}
